import { MapPin, Phone, Mail, ExternalLink } from "lucide-react";

const footerStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;600;700&family=Noto+Serif:ital,wght@0,400;0,600;1,400&display=swap');

  .footer-shell {
    background: linear-gradient(180deg, #0f0000 0%, #1a0000 60%, #0a0000 100%);
    position: relative;
    overflow: hidden;
  }

  /* top gold bar */
  .footer-shell::before {
    content: '';
    display: block; height: 3px;
    background: linear-gradient(90deg, transparent, #C9920A, #F0C040, #C9920A, transparent);
  }

  /* ambient glow orbs */
  .footer-orb-left {
    position: absolute; top: -120px; left: -120px;
    width: 380px; height: 380px; border-radius: 50%;
    background: radial-gradient(circle, rgba(139,0,0,0.18), transparent 65%);
    pointer-events: none;
  }
  .footer-orb-right {
    position: absolute; bottom: -100px; right: -100px;
    width: 320px; height: 320px; border-radius: 50%;
    background: radial-gradient(circle, rgba(201,146,10,0.1), transparent 65%);
    pointer-events: none;
  }

  /* subtle star tile */
  .footer-bg-pattern {
    position: absolute; inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23C9920A' fill-opacity='0.03'%3E%3Cpath d='M30 0l2.5 27.5H60L37 43l8.5 27.5L30 54l-15.5 16.5L23 43 0 27.5h27.5z'/%3E%3C/g%3E%3C/svg%3E");
    background-size: 60px 60px;
    pointer-events: none;
  }

  .footer-inner {
    max-width: 1150px; margin: 0 auto;
    padding: 4.5rem 2rem 0;
    position: relative; z-index: 1;
  }

  /* ── TOP: TEMPLE BANNER ── */
  .footer-banner {
    text-align: center;
    padding-bottom: 3.5rem;
    border-bottom: 1px solid rgba(201,146,10,0.15);
    margin-bottom: 3.5rem;
  }

  .footer-lamp { font-size: 2.8rem; animation: lampGlow 3s ease-in-out infinite; }

  @keyframes lampGlow {
    0%,100% { filter: drop-shadow(0 0 8px rgba(240,192,64,0.6)); }
    50% { filter: drop-shadow(0 0 22px rgba(240,192,64,1)) drop-shadow(0 0 40px rgba(240,192,64,0.3)); }
  }

  .footer-temple-name {
    font-family: 'Cinzel Decorative', serif;
    font-size: 1.65rem; font-weight: 700;
    color: #F0C040;
    text-shadow: 0 0 30px rgba(240,192,64,0.3), 1px 2px 6px rgba(0,0,0,0.8);
    margin: 0.75rem 0 0.4rem;
    line-height: 1.3;
  }

  .footer-temple-sub {
    font-family: 'Cinzel', serif;
    font-size: 0.72rem; letter-spacing: 0.3em; text-transform: uppercase;
    color: rgba(201,146,10,0.7); margin-bottom: 1.25rem;
  }

  .footer-ornament {
    display: flex; align-items: center; justify-content: center; gap: 12px;
    color: #C9920A; font-size: 0.95rem; letter-spacing: 0.5em;
  }
  .footer-ornament::before,
  .footer-ornament::after {
    content: ''; height: 1px; width: 70px;
    background: linear-gradient(90deg, transparent, #C9920A, transparent);
  }

  .footer-tagline {
    font-family: 'Noto Serif', serif;
    font-style: italic; font-size: 0.95rem;
    color: rgba(200,160,100,0.65); margin-top: 1.1rem;
    letter-spacing: 0.02em;
  }

  /* ── GRID ── */
  .footer-grid {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1.3fr;
    gap: 3rem;
    padding-bottom: 3.5rem;
    border-bottom: 1px solid rgba(201,146,10,0.12);
  }

  .footer-col-title {
    font-family: 'Cinzel', serif;
    font-size: 0.72rem; font-weight: 700;
    letter-spacing: 0.25em; text-transform: uppercase;
    color: #C9920A; margin-bottom: 1.4rem;
    display: flex; align-items: center; gap: 10px;
  }
  .footer-col-title::after {
    content: ''; flex: 1; height: 1px;
    background: linear-gradient(90deg, rgba(201,146,10,0.4), transparent);
  }

  /* Timing rows */
  .timing-row {
    display: flex; align-items: center; gap: 10px;
    margin-bottom: 0.85rem;
  }
  .timing-badge {
    font-family: 'Cinzel', serif; font-size: 0.6rem;
    font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
    color: #C9920A; background: rgba(201,146,10,0.1);
    border: 1px solid rgba(201,146,10,0.25);
    padding: 2px 8px; border-radius: 2px; white-space: nowrap; flex-shrink: 0;
  }
  .timing-time {
    font-family: 'Noto Serif', serif;
    font-size: 0.9rem; color: #d4b896;
  }

  /* Quick links */
  .footer-links { list-style: none; padding: 0; margin: 0; }
  .footer-links li { margin-bottom: 0.65rem; }
  .footer-links li a {
    font-family: 'Noto Serif', serif; font-size: 0.95rem;
    color: rgba(212,184,150,0.75); text-decoration: none;
    display: flex; align-items: center; gap: 8px;
    transition: all 0.25s;
  }
  .footer-links li a::before {
    content: '✦'; font-size: 0.5rem; color: #C9920A;
    opacity: 0; transform: translateX(-6px);
    transition: all 0.25s;
  }
  .footer-links li a:hover {
    color: #F0C040; padding-left: 6px;
  }
  .footer-links li a:hover::before { opacity: 1; transform: translateX(0); }

  /* Contact info */
  .contact-row {
    display: flex; align-items: flex-start; gap: 12px;
    margin-bottom: 1.1rem;
  }
  .contact-icon-wrap {
    width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
    background: linear-gradient(135deg, rgba(139,0,0,0.4), rgba(70,0,0,0.7));
    border: 1px solid rgba(201,146,10,0.3);
    display: flex; align-items: center; justify-content: center;
    margin-top: 1px;
    transition: all 0.25s;
  }
  .contact-row:hover .contact-icon-wrap {
    border-color: #C9920A;
    box-shadow: 0 0 12px rgba(201,146,10,0.25);
  }
  .contact-text {
    font-family: 'Noto Serif', serif; font-size: 0.92rem;
    color: #d4b896; line-height: 1.6;
  }
  .contact-text a {
    color: #d4b896; text-decoration: none; transition: color 0.2s;
  }
  .contact-text a:hover { color: #F0C040; }

  /* ── BOTTOM BAR ── */
  .footer-bottom {
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 1rem;
    padding: 1.5rem 2rem;
    max-width: 1150px; margin: 0 auto;
    position: relative; z-index: 1;
  }

  .footer-copy {
    font-family: 'Noto Serif', serif; font-style: italic;
    font-size: 0.82rem; color: rgba(201,146,10,0.45);
  }

  .footer-copy span { color: rgba(201,146,10,0.65); }

.footer-made {
  font-family: 'Cinzel', serif;
  font-size: 0.62rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(201,146,10,0.3);
}

.footer-brand {
  color:#F0C040;   /* dark color */
  font-weight: 600;
}

  @media (max-width: 768px) {
    .footer-grid { grid-template-columns: 1fr; gap: 2.5rem; }
    .footer-temple-name { font-size: 1.3rem; }
    .footer-bottom { flex-direction: column; text-align: center; }
  }
`;

const Footer = () => (
  <>
    <style>{footerStyles}</style>
    <footer className="footer-shell">
      <div className="footer-bg-pattern" />
      <div className="footer-orb-left" />
      <div className="footer-orb-right" />

      <div className="footer-inner">

        {/* ── BANNER ── */}
        <div className="footer-banner">
          <div className="footer-lamp">🪔</div>
          <h2 className="footer-temple-name">
            Sri La Sri Ayyalanandha Siddhar
          </h2>
          <p className="footer-temple-sub">Jeeva Samadhi Madalayam -Dhiyana Mandabam · Vellore</p>
          <div className="footer-ornament">✦ ✦ ✦</div>
          <p className="footer-tagline">
            A sacred abode of peace, devotion, and divine blessings
          </p>
        </div>

        {/* ── THREE COLUMN GRID ── */}
        <div className="footer-grid">

          {/* Col 1 — Timings */}
          <div>
            <p className="footer-col-title">Darshan Timings</p>
            <div className="timing-row">
              <span className="timing-badge">Morning</span>
              <span className="timing-time">7:00 AM – 12:00 Noon</span>
            </div>
            <div className="timing-row">
              <span className="timing-badge">Evening</span>
              <span className="timing-time">5:30 PM – 7:30 PM</span>
            </div>
            <div style={{ marginTop: "1.5rem" }}>
              <p className="footer-col-title">Special Days</p>
              <p style={{ fontFamily: "'Noto Serif', serif", fontStyle: "italic", fontSize: "0.88rem", color: "rgba(200,160,100,0.65)", lineHeight: 1.8 }}>
                Every Tuesday · Pournami<br />
                Amavasai · Sadhayam · Anusham
              </p>
            </div>
          </div>

          {/* Col 2 — Quick Links */}
          <div>
            <p className="footer-col-title">Quick Links</p>
            <ul className="footer-links">
              {[
                { label: "Home", href: "#home" },
                { label: "Special Poojas", href: "#poojas" },
                { label: "Gallery", href: "#gallery" },
                { label: "Contact Us", href: "#contact" },
              ].map((l) => (
                <li key={l.href}>
                  <a href={l.href}>{l.label}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Contact */}
          <div>
            <p className="footer-col-title">Contact Us</p>

            <div className="contact-row">
              <div className="contact-icon-wrap">
                <MapPin size={15} color="#C9920A" strokeWidth={1.8} />
              </div>
              <p className="contact-text">
                No. 38, Beri Kaliamman Kovil Street,<br />
                Beri Pettai, Vellore – 632004
              </p>
            </div>

            <div className="contact-row">
              <div className="contact-icon-wrap">
                <Phone size={15} color="#C9920A" strokeWidth={1.8} />
              </div>
              <p className="contact-text">
                <a href="tel:+919443313744">+91 94433 13744</a><br />
                <a href="tel:+919894481590">+91 98944 81590</a>
              </p>
            </div>

            <div className="contact-row">
              <div className="contact-icon-wrap">
                <Mail size={15} color="#C9920A" strokeWidth={1.8} />
              </div>
              <p className="contact-text">
                <a href="mailto:contact@jeevasamadhimadalayam.in">
                contact@jeevasamadhimadalayam.in
                </a>
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* ── BOTTOM BAR ── */}
      <div className="footer-bottom">
        <p className="footer-copy">
          © {new Date().getFullYear()}{" "}
          <span>Sri La Sri Ayyalanandha Siddhar Jeeva Samadhi Madam.</span>{" "}
          All Rights Reserved.
        </p>
      <p className="footer-made">
  Designed & Developed by <span className="footer-brand">Optywize</span> 🪔
</p>
      </div>

    </footer>
  </>
);

export default Footer;